import _ from 'lodash'
import { useContext, useEffect, useState } from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { SpaceBox } from 'components/Box'
import Filter, { FilterValue } from 'components/Filter'
import { SelectAutocomplete, StackForm } from 'components/Form'
import Loading from 'components/Loading'
import { SelectOption } from 'components/Select'
import { WorkspaceBidderContext } from 'features/globalSetup/contexts/WorkspaceBidderContext'
import { Schema } from '../../form/schema'
import { DomainBidderOption } from '../../hooks/useGetDomainBiddersForSelect'
import { AdUnitsTable } from '../AdUnitsTable'
import { DomainBidderFields } from './sections/DomainBidderFields'
import { useDomainBidderData } from './useDomainBidderData'
import { getRelatedDomainId, optionsMapper } from './utils'

type SchemaSlice = Pick<Schema, 'domainIds'>

export const FormBody = (): JSX.Element => {
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'prebid.bidders.edit.bidParamsAndConnection',
  })
  const { setCurrentDomainBidder, setValid } = useContext(WorkspaceBidderContext)
  const [tableFilters, setTableFilters] = useState<FilterValue[]>([])

  const { domainBidder, loading, options } = useDomainBidderData()

  const { control, getValues, setValue } = useFormContext<SchemaSlice>()
  const {
    domainIds: { domainId },
  } = getValues()
  const formState = useFormState<SchemaSlice>()
  const { errors } = formState

  useEffect(() => {
    setTableFilters([])
  }, [domainId])

  useEffect(() => {
    if (setValid !== undefined) {
      setValid(_.isEmpty(errors))
    }
  }, [formState])

  if (!domainBidder || loading) {
    return <Loading />
  }

  const handleChangeDomain = (selectedOption: SelectOption<DomainBidderOption>): void => {
    if (setCurrentDomainBidder) {
      setCurrentDomainBidder(domainBidder)
    }

    setValue('domainIds.domainBidderId', selectedOption)
    setValue('domainIds.domainId', getRelatedDomainId(selectedOption.value, options))
  }

  return (
    <>
      <StackForm pb={3}>
        <SelectAutocomplete<SchemaSlice, DomainBidderOption>
          control={control}
          label={t('domainSection.domain')}
          name='domainIds.domainBidderId'
          onChange={(selectedOption: SelectOption<DomainBidderOption>): void =>
            handleChangeDomain(selectedOption)
          }
          options={optionsMapper(options)}
        />

        <>
          <DomainBidderFields
            alerts={domainBidder.alerts}
            s2sAvailable={domainBidder.workspaceBidder.bidder.s2sAvailable}
            workspaceBidderPriority={domainBidder.workspaceBidder.priority}
          />

          <SpaceBox />

          <Filter
            id='adUnitsTableFilter'
            onChange={setTableFilters}
            placeholder={t('filter')}
            values={tableFilters}
          />
        </>
      </StackForm>

      <AdUnitsTable
        domainBidder={domainBidder}
        filters={tableFilters}
      />
    </>
  )
}
